import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { addDays, dubaiToday, fmtTime, weekdayMon0, WEEKDAYS, ymdLong, ymdShort } from "@/lib/format";
import { PageHeader, StatusBadge } from "@/components/admin/StatusBadge";
import { RegistrationSheet } from "@/components/admin/RegistrationSheet";

function range(view, anchor) {
  if (view === "day") return [anchor, anchor];
  if (view === "week") {
    const s = addDays(anchor, -weekdayMon0(anchor));
    return [s, addDays(s, 6)];
  }
  const first = `${anchor.slice(0, 7)}-01`;
  const [y, m] = [+anchor.slice(0, 4), +anchor.slice(5, 7)];
  return [first, `${anchor.slice(0, 7)}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, "0")}`];
}

function shiftAnchor(view, anchor, dir) {
  if (view === "day") return addDays(anchor, dir);
  if (view === "week") return addDays(anchor, 7 * dir);
  const [y, m] = [+anchor.slice(0, 4), +anchor.slice(5, 7)];
  const d = new Date(Date.UTC(y, m - 1 + dir, 1));
  return d.toISOString().slice(0, 10);
}

function Chip({ a, onOpen, full }) {
  return (
    <button onClick={() => onOpen(a.id)} className={`w-full text-left rounded-lg px-2 py-1 text-[11px] leading-tight border transition-colors ${a.status === "cancelled" ? "bg-slate-50 text-slate-400 line-through border-slate-200" : "bg-white border-[rgba(20,35,55,0.10)] hover:border-[#0B1F3A]/40 text-[#172033]"}`} data-testid={`calendar-appt-${a.id}`}>
      <span className="font-semibold">{fmtTime(a.start_utc)}</span> {a.full_name}
      {full && <span className="ml-2"><StatusBadge status={a.status} /></span>}
    </button>
  );
}

export default function CalendarView() {
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(dubaiToday());
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [start, end] = useMemo(() => range(view, anchor), [view, anchor]);

  const load = useCallback(() => {
    api.get("/admin/appointments", { params: { start, end } }).then((r) => setItems(r.data));
  }, [start, end]);

  useEffect(() => {
    load();
  }, [load]);

  const byDay = useMemo(() => items.reduce((acc, a) => ({ ...acc, [a.local_date]: [...(acc[a.local_date] || []), a] }), {}), [items]);
  const title = view === "day" ? ymdLong(anchor) : view === "week" ? `${ymdShort(start)} – ${ymdShort(end)}` : new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${start}T00:00:00Z`));
  const today = dubaiToday();
  const days = [];
  for (let d = start; d <= end; d = addDays(d, 1)) days.push(d);

  return (
    <div data-testid="admin-calendar">
      <PageHeader title="Calendar" subtitle="All times in Dubai time (GST, UTC+4).">
        <Tabs value={view} onValueChange={setView}>
          <TabsList className="rounded-full">
            {["day", "week", "month"].map((v) => <TabsTrigger key={v} value={v} className="rounded-full capitalize" data-testid={`calendar-view-${v}`}>{v}</TabsTrigger>)}
          </TabsList>
        </Tabs>
      </PageHeader>
      <div className="flex items-center gap-2 mb-5">
        <button onClick={() => setAnchor(shiftAnchor(view, anchor, -1))} className="h-9 w-9 grid place-items-center rounded-full border bg-white" aria-label="Previous" data-testid="admin-calendar-prev"><ChevronLeft size={16} /></button>
        <button onClick={() => setAnchor(shiftAnchor(view, anchor, 1))} className="h-9 w-9 grid place-items-center rounded-full border bg-white" aria-label="Next" data-testid="admin-calendar-next"><ChevronRight size={16} /></button>
        <button onClick={() => setAnchor(today)} className="rounded-full border bg-white px-4 h-9 text-sm" data-testid="admin-calendar-today">Today</button>
        <h2 className="ml-2 font-display text-lg font-semibold text-[#172033]" data-testid="admin-calendar-title">{title}</h2>
      </div>

      {view === "day" && (
        <div className="glass rounded-3xl p-6 space-y-2" data-testid="calendar-day-view">
          {(byDay[anchor] || []).length === 0 && <p className="text-sm text-[#697386]">No appointments on this day.</p>}
          {(byDay[anchor] || []).map((a) => <Chip key={a.id} a={a} onOpen={setOpenId} full />)}
        </div>
      )}

      {view !== "day" && (
        <div className="glass rounded-3xl p-3 sm:p-4 overflow-x-auto" data-testid={`calendar-${view}-view`}>
          <div className="grid grid-cols-7 gap-2 min-w-[760px]">
            {WEEKDAYS.map((w) => <div key={w} className="text-[11px] font-semibold uppercase tracking-wider text-[#697386] px-2 pb-1">{w}</div>)}
            {view === "month" && Array.from({ length: weekdayMon0(start) }).map((_, i) => <div key={`e${i}`} />)}
            {days.map((d) => (
              <div key={d} className={`rounded-2xl border p-2 ${view === "week" ? "min-h-[260px]" : "min-h-[110px]"} ${d === today ? "border-[#C7A76C]/60 bg-[#C7A76C]/5" : "border-[rgba(20,35,55,0.08)] bg-white/50"}`} data-testid={`admin-cal-day-${d}`}>
                <button onClick={() => { setAnchor(d); setView("day"); }} className="text-xs font-semibold text-[#172033] hover:text-[#C7A76C]">{+d.slice(8)}</button>
                <div className="mt-1.5 space-y-1">{(byDay[d] || []).map((a) => <Chip key={a.id} a={a} onOpen={setOpenId} />)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <RegistrationSheet id={openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}
